import { Ref, getCurrentInstance, ComponentInternalInstance } from '@vue/composition-api';
import smh, { SMH } from '@/renderer/scripts/smh';
import { IS_BROWSER } from '@/common/constant/platform';
import storage from '@/renderer/scripts/utils/storage';
import tips from '@/renderer/scripts/utils/tips';
import { Sort } from '@/renderer/components/SpacePage/compositions/useSort';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';

interface PreviewItem {
  name: string;
  type: SMH.FileItemType;
  path: string[];
  previewByDoc?: boolean;
  fileType?: string;
  spaceId?: SMH.SpaceId;
  spaceOrgId?: number;
  spaceTag?: SMH.SpaceTag;
}
type Handler = {
  methods: {
    openPreview: (
      item: PreviewItem,
      previewPage: number,
      options?: {
        historyId?: number;
        teamId?: number;
        esign?: boolean; // true 表示开启电子签弹窗
        sort?: Sort;
        showList?: boolean; // true 表示展示预览列表
        spaceTag?: string;
        isHistoryVersion?: boolean;
      },
    ) => void;
    openEdit: (
      item: PreviewItem,
      sort?: Sort,
      showList?: boolean,
    ) => // true 表示展示预览列表
    void;
  };
};
export default function handler(spaceId?: Ref<SpaceId>, spaceOrgId?: Ref<number>): Handler {
  // 当前组件
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const { $route, $router, $store } = vm;
  const methods = {
    async checkPreviewTimes(
      item: PreviewItem,
      options: {
        historyId?: number;
        only?: string;
        teamId?: number;
        esign?: boolean;
      },
    ): Promise<boolean> {
      try {
        const currPath = (item.path as string[]).join('/');
        const shareToken = ($route.params?.shareToken as string) || ($route.query?.shareToken as string);
        // TODO 暂时最小改动修复spaceOrgId,后续要全部优化到item.SpaceOrgId中去。
        let spaceOrgIdComputed =
          spaceOrgId?.value || item?.spaceOrgId || vm.$route?.query?.spaceOrgId || $store.getters.orgId;
        if (spaceOrgId?.value && spaceOrgId.value !== -1) {
          spaceOrgIdComputed = spaceOrgId.value;
        }
        await smh.commonAPI.checkPreviewAndDownloadTime({
          filePath: currPath,
          spaceId: item?.spaceId || spaceId?.value,
          shareToken: shareToken as string,
          historyId: options.historyId,
          check_preview_and_download_times: 1,
          spaceOrgId: spaceOrgIdComputed,
        });
        /*
          previewCount:预览总次数 如果是0则无限制；
          previewCountUsed:预览已使用的次数；
          downloadCount: 下载总次数 如果是0则无限制；
          downloadCountUsed:下载已使用的次数
        */
        return true;
      } catch (error: any) {
        tips.error(error as MsgError);
        return false;
      }
    },
    /**
     * 浏览器端预览
     * @param item
     * @param previewPage
     * @param historyId
     * @param teamId
     */
    async openPreviewInBrowser(
      item: PreviewItem,
      previewPage: number,
      options: {
        historyId?: number;
        teamId?: number;
        esign?: boolean; // true 表示开启电子签弹窗
        sort?: Sort;
        showList?: boolean; // true 表示展示预览列表
        isHistoryVersion?: boolean;
      },
      spaceTag?: string,
    ) {
      const currPath = (item.path as string[]).slice(0, -1).join('/');
      const path = item.previewByDoc ? '/web/preview/doc' : '/web/preview/media';
      let descending: undefined | string;
      if (options.sort !== undefined) {
        descending = options.sort.descending ? '1' : '0';
      }
      const query: Record<string, string | undefined> = {
        name: item.name,
        path: currPath,
        previewPage: `${previewPage}`,
        spaceId: item?.spaceId || spaceId?.value,
        from: $route.name as string,
        spaceOrgId: spaceOrgId?.value || item?.spaceOrgId || vm.$route?.query?.spaceOrgId || $store.getters.orgId,
        spaceTag: spaceTag ?? item.spaceTag,
        sortBy: options.sort?.sortBy,
        descending,
        sortType: options.sort?.sortType,
        showList: !options.showList ? '0' : '1', // undefine时传0
        // docPreviewMode---预览模式
        ...(vm.$route.query.docPreviewMode && { docPreviewMode: vm.$route.query.docPreviewMode as string }),
      };

      // 预览图片列表时，分享链接根目录需要单独处理路径
      if ($route.name === 'share' && !item.previewByDoc && item?.fileType === 'image') {
        query.imgShareRootPath = currPath;
        const rootPath = storage.get<string[]>(storage.StoreKey.SHARE_LINK_ROOT_PATH) || [];
        if (Array.isArray(rootPath) && rootPath.join('/') === currPath) {
          query.imgShareRootPath = '';
        }
      }
      const shareToken = ($route.params?.shareToken as string) || ($route.query?.shareToken as string);
      shareToken && (query.shareToken = shareToken as string);
      options.historyId && (query.historyId = String(options.historyId));
      options.teamId && (query.teamId = String(options.teamId));
      options.esign && (query.esign = '1');
      options.isHistoryVersion && (query.isHistoryVersion = '1');
      const newpage = $router?.resolve({
        path,
        query,
      });
      if ($store.getters.isInWeMeetApp || $store.getters.isTclApp) {
        const currRoute = {
          name: $router.currentRoute.name,
          query: $router.currentRoute.query,
          params: $router.currentRoute.params,
        };
        storage.set(storage.StoreKey.ROUTE_BEFORE_PREVIEW, currRoute);
        const { name, query } = newpage.route;
        dispatchRouter({
          name: name as string,
          query,
        });
      } else {
        const previewPageType = vm.$route.query.preview_page;
        const target = previewPageType === 'current' ? '_self' : '_blank';
        // safari上不加setTimeout无法打开窗口
        setTimeout(() => {
          dispatchRouter(newpage?.href, target);
        });
      }
    },
    async openPreview(
      item: PreviewItem,
      previewPage: number,
      options: {
        historyId?: number;
        teamId?: number;
        esign?: boolean; // true 表示开启电子签弹窗
        sort?: Sort;
        showList?: boolean;
        spaceTag?: string;
        isHistoryVersion?: boolean;
      } = {
        esign: false,
      },
    ) {
      const currPath = (item.path as string[]).slice(0, -1).join('/');
      let spaceTag = item?.spaceTag || options?.spaceTag || ($route.query?.spaceTag as string);
      // 个人空间、企业空间、协作空间列表没有返回spaceTag时，根据路由手动设置一下
      if ($route.name === 'personalSpace') {
        spaceTag = SMH.SpaceTag.personal;
      } else if ($route.name === 'teamSpace') {
        spaceTag = SMH.SpaceTag.team;
      } else if ($route.name === 'collaborationSpaceList') {
        spaceTag = SMH.SpaceTag.group;
      }
      Object.assign(item, {
        spaceTag,
      });
      // 统一上报预览操作
      reporter.report('file_preview', {
        preview_type: item?.fileType,
        file_name: item.name,
      });
      const canPreview = await this.checkPreviewTimes(item, options);
      if (!canPreview) {
        return;
      }
      if (IS_BROWSER) {
        // 用于预览回退时document.title为打开预览前的值
        $store.commit('setDefaultTitle', document.title);
        this.openPreviewInBrowser(item, previewPage, options, spaceTag);
      } else {
        const currRoute = {
          name: $router.currentRoute.name,
          query: $router.currentRoute.query,
          params: $router.currentRoute.params,
        };
        storage.set(storage.StoreKey.ROUTE_BEFORE_PREVIEW, currRoute);
        const routerName = item.previewByDoc ? 'doc' : 'media';
        let descending: undefined | string;
        if (options.sort !== undefined) {
          descending = options.sort.descending ? '1' : '0';
        }
        const temp = {
          name: item.name,
          path: currPath,
          previewPage: `${previewPage}`,
          spaceId: item?.spaceId || spaceId?.value,
          from: $route.name as string,
          spaceOrgId: spaceOrgId?.value || item?.spaceOrgId || $store.getters.orgId,
          sortBy: options.sort?.sortBy,
          descending,
          sortType: options.sort?.sortType,
          showList: !options.showList ? '0' : '1',
          // undefined时传0
          spaceTag,
        };
        options.historyId &&
          Object.assign(temp, {
            historyId: String(options.historyId),
          });
        options.teamId &&
          Object.assign(temp, {
            teamId: String(options.teamId),
          });
        options.esign &&
          Object.assign(temp, {
            esign: '1',
          });
        options.isHistoryVersion &&
          Object.assign(temp, {
            isHistoryVersion: '1',
          });

        // 当前是Preview页面的话需要强制刷新
        if (vm.$route.path.includes('/preview/')) {
          $router
            .replace({
              name: 'refresh',
              query: {
                ...temp,
                lastRouteName: routerName,
              },
            })
            .catch((err: any) => err);
        } else {
          $router
            .replace({
              name: routerName,
              query: temp,
            })
            .catch();
        }
      }
    },

    async openEdit(item: PreviewItem, sort?: Sort, showList?: boolean) {
      const currPath = (item.path as string[]).slice(0, -1).join('/');
      let descending: undefined | string;
      if (sort !== undefined) {
        descending = sort.descending ? '1' : '0';
      }
      // 同预览：需要设置spaceTag
      let spaceTag = item?.spaceTag || ($route.query?.spaceTag as string);
      if ($route.name === 'personalSpace') {
        spaceTag = SMH.SpaceTag.personal;
      } else if ($route.name === 'teamSpace') {
        spaceTag = SMH.SpaceTag.team;
      } else if ($route.name === 'collaborationSpaceList') {
        spaceTag = SMH.SpaceTag.group;
      }
      const query: Record<string, string | undefined> = {
        name: item.name,
        path: currPath,
        spaceId: spaceId?.value,
        from: $route.name as string,
        spaceOrgId: spaceOrgId?.value || item?.spaceOrgId || $store.getters.orgId,
        spaceTag,
        sortBy: sort?.sortBy,
        descending,
        sortType: sort?.sortType,
        showList: !showList ? '0' : '1', // undefined时传0
      };

      const shareToken = ($route.params?.shareToken as string) || ($route.query?.shareToken as string);
      shareToken && (query.shareToken = shareToken as string);
      const newpage = $router?.resolve({
        path: '/web/preview/edit',
        query,
      });
      // 统一上报编辑操作
      reporter.report('file_edit', {
        file_type: item?.fileType,
        file_name: item.name,
      });
      if (IS_BROWSER && !$store.getters.isInWeMeetApp && !$store.getters.isTclApp) {
        // safari上不加setTimeout无法打开窗口
        setTimeout(() => {
          dispatchRouter(newpage?.href, '_blank');
        });
      } else {
        const currRoute = {
          name: $router.currentRoute.name,
          query: $router.currentRoute.query,
          params: $router.currentRoute.params,
        };
        storage.set(storage.StoreKey.ROUTE_BEFORE_PREVIEW, currRoute);
        $router
          .replace({
            name: 'edit',
            query,
          })
          .catch();
      }
    },
  };
  return {
    methods,
  };
}
